import { useEffect, useRef, useState } from "react";

type Props = {
  src: string;
  poster?: string;
  className?: string;
  /**
   * Optional portrait variant. When provided, the component will swap to it
   * on viewports where `(orientation: portrait)` matches. The `src` prop is
   * the landscape default. Both files are rendered from the SAME Remotion
   * composition (`RaftingHero`) — the composition reads its own canvas
   * dimensions via `useVideoConfig()` and emits text sized for that aspect.
   *
   * If omitted, behaves exactly like the original single-source video.
   */
  srcMobile?: string;
};

/**
 * Full-bleed background video for the hero section.
 *
 * - Auto-plays muted on mount (required for browser auto-play policy).
 * - Loops indefinitely.
 * - Honors prefers-reduced-motion: shows poster only, video stays paused.
 * - Fades in on first `loadeddata` for a smooth swap from the poster.
 * - Pauses when off-screen (saves CPU on long pages).
 * - Picks landscape vs portrait MP4 from a media query so the baked-in text
 *   never gets clipped by `object-fit: cover` on a phone.
 */
export function HeroVideo({ src, poster, className, srcMobile }: Props) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [loaded, setLoaded] = useState(false);
  const [activeSrc, setActiveSrc] = useState(src);
  // Polish round 2 — item #20: opt-out of the hero video on slow connections
  // and on devices with Data Saver enabled. Feature-detected: browsers that
  // don't expose `navigator.connection` (Safari, Firefox) keep the default
  // video behavior because we can't infer their connection state.
  const [skipVideo, setSkipVideo] = useState(() => {
    if (typeof navigator === "undefined") return false;
    type NetInfo = { effectiveType?: string; saveData?: boolean };
    const conn = (navigator as Navigator & { connection?: NetInfo }).connection;
    if (!conn) return false;
    if (conn.saveData) return true;
    return conn.effectiveType === "2g" || conn.effectiveType === "slow-2g";
  });

  // Pick landscape vs portrait variant from the orientation media query.
  // Updates if the device rotates or the viewport flips during dev/resize.
  useEffect(() => {
    if (!srcMobile) return;
    const mql = window.matchMedia("(orientation: portrait)");
    const apply = (matches: boolean) => {
      setActiveSrc(matches ? srcMobile : src);
      // On change, force the <video> element to re-load the new file.
      const v = videoRef.current;
      if (v) {
        setLoaded(false);
        v.load();
      }
    };
    apply(mql.matches);
    const onChange = (e: MediaQueryListEvent) => apply(e.matches);
    mql.addEventListener?.("change", onChange);
    return () => mql.removeEventListener?.("change", onChange);
  }, [src, srcMobile]);

  // item #20: keep tracking connection changes after mount (a phone that
  // started on Wi-Fi and dropped to cellular will start reporting "2g" or
  // toggle saveData mid-session). We re-evaluate via `change` listener.
  useEffect(() => {
    if (typeof navigator === "undefined") return;
    type NetInfo = {
      effectiveType?: string;
      saveData?: boolean;
      addEventListener?: (t: string, cb: () => void) => void;
      removeEventListener?: (t: string, cb: () => void) => void;
    };
    const conn = (navigator as Navigator & { connection?: NetInfo }).connection;
    if (!conn || !conn.addEventListener) return;
    const reevaluate = () => {
      const slow =
        !!conn.saveData ||
        conn.effectiveType === "2g" ||
        conn.effectiveType === "slow-2g";
      setSkipVideo(slow);
    };
    conn.addEventListener("change", reevaluate);
    return () => conn.removeEventListener?.("change", reevaluate);
  }, []);

  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;

    const reduceMotion = window.matchMedia?.(
      "(prefers-reduced-motion: reduce)",
    ).matches;
    if (reduceMotion) return; // poster only, no playback

    // IntersectionObserver: pause when not visible.
    // Mobile round 2 — item #2: threshold lowered to 0.05 so the video pauses
    // sooner as the hero scrolls away on phones, saving CPU/battery.
    const io = new IntersectionObserver(
      (entries) => {
        for (const e of entries) {
          if (e.isIntersecting) v.play().catch(() => {});
          else v.pause();
        }
      },
      { threshold: 0.05 },
    );
    io.observe(v);

    // Mobile round 2 — item #1: pause when the tab is hidden (background
    // multitasking on mobile) and resume on return. Skipped while the user
    // has reduced motion (we already early-returned above for that case).
    const onVisibility = () => {
      if (document.hidden) {
        v.pause();
      } else {
        // Only resume if the video is still in view; the IO will catch the
        // other case on next intersection callback anyway, but this avoids
        // a brief unnecessary play() that would immediately be paused.
        v.play().catch(() => {});
      }
    };
    document.addEventListener("visibilitychange", onVisibility);

    return () => {
      io.disconnect();
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, [skipVideo]);

  // Polish round 2 — item #22: Spacebar toggles play/pause when focus is on
  // the body or the <video> itself. Skipped when prefers-reduced-motion is
  // set (we never autoplay in that case, so a manual play would contradict
  // the user's stated preference) and when the user is typing in a field.
  useEffect(() => {
    if (skipVideo) return;
    const reduceMotion = window.matchMedia?.(
      "(prefers-reduced-motion: reduce)",
    ).matches;
    if (reduceMotion) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.code !== "Space") return;
      const v = videoRef.current;
      if (!v) return;
      const target = e.target as HTMLElement | null;
      // Don't hijack space inside form fields, buttons, or contenteditable.
      const tag = target?.tagName;
      if (
        tag === "INPUT" ||
        tag === "TEXTAREA" ||
        tag === "SELECT" ||
        tag === "BUTTON" ||
        tag === "A" ||
        target?.isContentEditable
      ) {
        return;
      }
      // Only react when focus is on the body or the video element itself —
      // anywhere else and we leave default scroll behavior alone.
      if (target !== document.body && target !== v) return;
      e.preventDefault();
      if (v.paused) v.play().catch(() => {});
      else v.pause();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [skipVideo]);

  // item #20: on slow connections / Data Saver, skip the video entirely and
  // render the poster as a static <img>. fetchpriority="high" matches the
  // <link rel="preload"> already in index.html so the LCP image starts
  // arriving as early as possible.
  if (skipVideo && poster) {
    return (
      <img
        src={poster}
        alt=""
        className={className}
        aria-hidden="true"
        // React 19 supports lowercase `fetchpriority` natively; older type
        // defs sometimes lag behind so we widen via a cast.
        {...({ fetchpriority: "high" } as { fetchpriority: "high" })}
        decoding="async"
        loading="eager"
        style={{ opacity: 1 }}
      />
    );
  }

  return (
    <video
      ref={videoRef}
      className={className}
      src={activeSrc}
      poster={poster}
      muted
      loop
      playsInline
      autoPlay
      preload="metadata"
      aria-hidden="true"
      // item #22 — make the element focusable so users can land on it with
      // Tab and then space-toggle. -1 keeps it out of the default tab order
      // but lets `focus()` and the keydown handler still address it.
      tabIndex={-1}
      onLoadedData={() => setLoaded(true)}
      style={{
        opacity: loaded ? 1 : 0,
        transition: "opacity 600ms ease",
      }}
    />
  );
}
